import React, { useState, useEffect } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";
import { addDataRefreshListener } from "../utils/events";
import { Child } from "../types/child";
import { getGoalForDate, getLocalDateString } from "../utils/goalUtils";

interface CalendarProps {
  user: User | null;
  darkMode: boolean;
  selectedChildId?: string;
  hideChildSelector?: boolean;
}

interface DailyLogEntry {
  id: string;
  date: string;
  time_spent_minutes: number;
  notes?: string;
  created_at?: string;
  child_id?: string;
  child_name?: string;
}

interface DailyLog {
  date: string;
  total_minutes: number;
  entries: DailyLogEntry[];
  hasNotes: boolean;
  children_data?: {
    [childId: string]: {
      name: string;
      minutes: number;
      dailyGoalAtTime?: number;
    };
  };
  daily_goal_at_time?: number; // Historical goal from when log was created
}

interface UserGoals {
  weekly_goal_minutes: number;
  monday_goal_minutes?: number | null;
  tuesday_goal_minutes?: number | null;
  wednesday_goal_minutes?: number | null;
  thursday_goal_minutes?: number | null;
  friday_goal_minutes?: number | null;
  saturday_goal_minutes?: number | null;
  sunday_goal_minutes?: number | null;
}

interface ChildGoals {
  [childId: string]: {
    weekly_goal_minutes: number;
    monday_goal_minutes?: number | null;
    tuesday_goal_minutes?: number | null;
    wednesday_goal_minutes?: number | null;
    thursday_goal_minutes?: number | null;
    friday_goal_minutes?: number | null;
    saturday_goal_minutes?: number | null;
    sunday_goal_minutes?: number | null;
  };
}

const Calendar: React.FC<CalendarProps> = ({
  user,
  darkMode,
  selectedChildId,
  hideChildSelector,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailyLogs, setDailyLogs] = useState<DailyLog[]>([]);
  const [userGoals, setUserGoals] = useState<UserGoals>({
    weekly_goal_minutes: 0,
  });
  const [childGoals, setChildGoals] = useState<ChildGoals>({});
  const [children, setChildren] = useState<Child[]>([]);
  const [treatmentType, setTreatmentType] = useState<"self" | "child">("self");
  // Local selection fallback for normal users (when parent doesn't pass selectedChildId)
  const [localSelectedChildId, setLocalSelectedChildId] = useState<
    string | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<{
    day: number;
    log: DailyLog | undefined;
  } | null>(null);
  const [userJoinDate, setUserJoinDate] = useState<string | null>(null);
  // Goal history: array of snapshots ordered by effective_from desc
  const [goalHistory, setGoalHistory] = useState<Array<{
    effective_from: string;
    child_id: string | null;
    sunday_goal_minutes: number | null;
    monday_goal_minutes: number | null;
    tuesday_goal_minutes: number | null;
    wednesday_goal_minutes: number | null;
    thursday_goal_minutes: number | null;
    friday_goal_minutes: number | null;
    saturday_goal_minutes: number | null;
  }>>([]);

  // Fetch treatment type first
  useEffect(() => {
    if (user) {
      fetchTreatmentType();
    }
  }, [user]);

  // Get calendar data for current month
  useEffect(() => {
    const loadAll = async () => {
      if (!user) return;
      setLoading(true);
      setDailyLogs([]); // Clear previous month's data
      try {
        const childrenList =
          treatmentType === "child" ? (await fetchChildren(true)) || [] : [];
        await fetchUserGoals(childrenList);
        await fetchCalendarData(childrenList);
        await fetchUserJoinDate();
        await fetchGoalHistory();
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadAll();
    } else {
      // Clean up state when user becomes null (e.g., during logout)
      setDailyLogs([]);
      setUserGoals({
        weekly_goal_minutes: 0,
      });
      setChildGoals({});
      setChildren([]);
      setUserJoinDate(null);
      setSelectedDay(null);
    }
  }, [user, currentDate, selectedChildId, localSelectedChildId, treatmentType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for global data refresh events to reload calendar
  useEffect(() => {
    const remove = addDataRefreshListener(() => {
      if (user) {
        fetchTreatmentType();
        fetchCalendarData();
        fetchUserJoinDate();
      }
    });
    return remove;
  }, [user, currentDate, selectedChildId, localSelectedChildId]);

  const fetchCalendarData = async (childrenListParam?: Child[]) => {
    if (!user) return;
    const effectiveSelectedChildId = selectedChildId ?? localSelectedChildId;

    const startOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    );
    const endOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    );

    try {
      // First, get all children for this user (use provided list if available)
      let childrenList = childrenListParam;
      if (!childrenList) {
        const { data: childrenData, error: childrenError } = await supabase
          .from("children")
          .select("*")
          .eq("user_id", user.id)
          .order("name");
        if (childrenError) throw childrenError;
        childrenList = childrenData || [];
        setChildren(childrenList);
      }

      // If treatment type is child and children exist, use child_logs, otherwise use legacy daily_logs
      if (treatmentType === "child" && childrenList.length > 0) {
        // If a specific child is selected, only fetch their logs
        if (effectiveSelectedChildId) {
          const { data, error } = await supabase
            .from("child_logs")
            .select(
              `
              id, date, time_spent_minutes, notes, created_at,
              child_id, daily_goal_at_time,
              children!inner(id, name)
            `,
            )
            .eq("child_id", effectiveSelectedChildId)
            .gte("date", getLocalDateString(startOfMonth))
            .lte("date", getLocalDateString(endOfMonth))
            .order("date", { ascending: true })
            .order("created_at", { ascending: true });

          if (error) throw error;

          // Group entries by date and sum up the time
          const groupedLogs: DailyLog[] = [];
          const logsByDate = new Map<string, DailyLogEntry[]>();

          (data || []).forEach((entry: any) => {
            const logEntry: DailyLogEntry = {
              id: entry.id,
              date: entry.date,
              time_spent_minutes: entry.time_spent_minutes,
              notes: entry.notes,
              created_at: entry.created_at,
              child_id: entry.children.id,
              child_name: entry.children.name,
            };

            if (!logsByDate.has(entry.date)) {
              logsByDate.set(entry.date, []);
            }
            logsByDate.get(entry.date)!.push(logEntry);
          });

          logsByDate.forEach((entries, date) => {
            const totalMinutes = entries.reduce(
              (sum, entry) => sum + entry.time_spent_minutes,
              0,
            );
            const hasNotes = entries.some(
              (entry) => entry.notes && entry.notes.trim().length > 0,
            );

            // Get historical goal from the most recent entry for this date
            const historicalGoal = (data || []).find(
              (entry: any) => entry.date === date,
            )?.daily_goal_at_time;

            groupedLogs.push({
              date,
              total_minutes: totalMinutes,
              entries,
              hasNotes,
              daily_goal_at_time: historicalGoal,
            });
          });

          setDailyLogs(groupedLogs);
        } else {
          // Show combined view for all children
          const { data, error } = await supabase
            .from("child_logs")
            .select(
              `
              id, date, time_spent_minutes, notes, created_at,
              daily_goal_at_time,
              children!inner(id, name)
            `,
            )
            .in(
              "child_id",
              childrenList.map((child) => child.id),
            )
            .gte("date", getLocalDateString(startOfMonth))
            .lte("date", getLocalDateString(endOfMonth))
            .order("date", { ascending: true })
            .order("created_at", { ascending: true });

          if (error) throw error;

          // Group entries by date and sum up the time
          const groupedLogs: DailyLog[] = [];
          const logsByDate = new Map<string, DailyLogEntry[]>();

          (data || []).forEach((entry: any) => {
            const logEntry: DailyLogEntry = {
              id: entry.id,
              date: entry.date,
              time_spent_minutes: entry.time_spent_minutes,
              notes: entry.notes,
              created_at: entry.created_at,
              child_id: entry.children.id,
              child_name: entry.children.name,
            };

            if (!logsByDate.has(entry.date)) {
              logsByDate.set(entry.date, []);
            }
            logsByDate.get(entry.date)!.push(logEntry);
          });

          logsByDate.forEach((entries, date) => {
            const totalMinutes = entries.reduce(
              (sum, entry) => sum + entry.time_spent_minutes,
              0,
            );
            const hasNotes = entries.some(
              (entry) => entry.notes && entry.notes.trim().length > 0,
            );

            // Group by child for detailed view
            const childrenData: {
              [childId: string]: {
                name: string;
                minutes: number;
                dailyGoalAtTime?: number;
              };
            } = {};
            entries.forEach((entry) => {
              if (entry.child_id && entry.child_name) {
                const originalEntry = (data || []).find(
                  (d: any) =>
                    d.children.id === entry.child_id && d.date === date,
                );
                if (!childrenData[entry.child_id]) {
                  childrenData[entry.child_id] = {
                    name: entry.child_name,
                    minutes: 0,
                    dailyGoalAtTime: originalEntry?.daily_goal_at_time,
                  };
                }
                childrenData[entry.child_id].minutes +=
                  entry.time_spent_minutes;
              }
            });

            groupedLogs.push({
              date,
              total_minutes: totalMinutes,
              entries,
              hasNotes,
              children_data: childrenData,
            });
          });

          setDailyLogs(groupedLogs);
        }
      } else {
        // No children, use legacy daily_logs
        const { data, error } = await supabase
          .from("daily_logs")
          .select(
            "id, date, time_spent_minutes, notes, created_at, daily_goal_at_time",
          )
          .eq("user_id", user.id)
          .gte("date", getLocalDateString(startOfMonth))
          .lte("date", getLocalDateString(endOfMonth))
          .order("date", { ascending: true })
          .order("created_at", { ascending: true });

        if (error) throw error;

        // Group entries by date and sum up the time
        const groupedLogs: DailyLog[] = [];
        const logsByDate = new Map<string, DailyLogEntry[]>();

        (data || []).forEach((entry: DailyLogEntry) => {
          if (!logsByDate.has(entry.date)) {
            logsByDate.set(entry.date, []);
          }
          logsByDate.get(entry.date)!.push(entry);
        });

        logsByDate.forEach((entries, date) => {
          const totalMinutes = entries.reduce(
            (sum, entry) => sum + entry.time_spent_minutes,
            0,
          );
          const hasNotes = entries.some(
            (entry) => entry.notes && entry.notes.trim().length > 0,
          );

          // Get historical goal from the most recent entry for this date
          const historicalGoal = (data || []).find(
            (entry: any) => entry.date === date,
          )?.daily_goal_at_time;

          groupedLogs.push({
            date,
            total_minutes: totalMinutes,
            entries,
            hasNotes,
            daily_goal_at_time: historicalGoal,
          });
        });

        setDailyLogs(groupedLogs);
      }
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching calendar data:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGoals = async (childrenListParam?: Child[]) => {
    if (!user) return;

    try {
      // Get user goals (legacy support)
      const { data, error } = await supabase
        .from("user_goals")
        .select(
          "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
        )
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setUserGoals(data);
      } else {
        // Only try to create default goals if we have a valid user session
        // Check if auth session is still valid before attempting to create goals
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.user?.id === user.id) {
          const defaultDaily = 240;
          const { error: insertError } = await supabase
            .from("user_goals")
            .insert([
              {
                user_id: user.id,
                monday_goal_minutes: defaultDaily,
                tuesday_goal_minutes: defaultDaily,
                wednesday_goal_minutes: defaultDaily,
                thursday_goal_minutes: defaultDaily,
                friday_goal_minutes: defaultDaily,
                saturday_goal_minutes: defaultDaily,
                sunday_goal_minutes: defaultDaily,
                weekly_goal_minutes: defaultDaily * 7,
              },
            ]);

          if (insertError && insertError.code !== "42501") {
            // Don't log RLS policy errors (42501) as they're expected during logout
            console.error("Error creating default goals:", insertError);
          }
        }
      }

      // Get child goals if treatment type is child and children exist
      const childrenForGoals = childrenListParam || children;
      if (treatmentType === "child" && childrenForGoals.length > 0) {
        const childIds = childrenForGoals.map((child) => child.id);
        const { data: childGoalsData, error: childGoalsError } = await supabase
          .from("child_goals")
          .select(
            "child_id, weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
          )
          .in("child_id", childIds);

        if (childGoalsError) throw childGoalsError;

        const goalsMap: ChildGoals = {};
        childGoalsData?.forEach((goal) => {
          goalsMap[goal.child_id] = {
            weekly_goal_minutes: goal.weekly_goal_minutes,
            monday_goal_minutes: (goal as any).monday_goal_minutes,
            tuesday_goal_minutes: (goal as any).tuesday_goal_minutes,
            wednesday_goal_minutes: (goal as any).wednesday_goal_minutes,
            thursday_goal_minutes: (goal as any).thursday_goal_minutes,
            friday_goal_minutes: (goal as any).friday_goal_minutes,
            saturday_goal_minutes: (goal as any).saturday_goal_minutes,
            sunday_goal_minutes: (goal as any).sunday_goal_minutes,
          };
        });

        setChildGoals(goalsMap);
      }
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching user goals:", error);
      }
    }
  };

  const fetchTreatmentType = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("user_data")
        .select("treatment_type")
        .eq("user_id", user.id)
        .single();

      if (error) throw error;

      setTreatmentType((data?.treatment_type as "self" | "child") || "self");
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching treatment type:", error);
      }
      setTreatmentType("self");
    }
  };

  const fetchChildren = async (
    returnList = false,
  ): Promise<Child[] | undefined> => {
    if (!user) return returnList ? [] : undefined;

    try {
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;

      setChildren(data || []);
      return returnList ? data || [] : undefined;
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching children:", error);
      }
      return returnList ? [] : undefined;
    }
  };

  const fetchUserJoinDate = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("user_data")
        .select("created_at")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setUserJoinDate(data.created_at);
      }
    } catch (error: any) {
      // Don't log RLS policy errors during logout
      if (error?.code !== "42501") {
        console.error("Error fetching user join date:", error);
      }
    }
  };

  const fetchGoalHistory = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from("goal_history")
        .select("effective_from, child_id, sunday_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes")
        .eq("user_id", user.id)
        .order("effective_from", { ascending: false });
      if (error) throw error;
      setGoalHistory(data || []);
    } catch {
      // Non-critical — fall back to current goals
    }
  };

  /**
   * Get the goals that were active on a specific past date by looking up goal_history.
   * Returns the snapshot with the highest effective_from that is <= date.
   * Falls back to current goals if no history exists.
   */
  const getGoalsForPastDate = (dateStr: string, childId: string | null): UserGoals | null => {
    const relevant = goalHistory.filter(
      (h) => h.child_id === childId && h.effective_from <= dateStr
    );
    if (relevant.length === 0) return null; // No history — caller uses current goals
    return relevant[0] as unknown as UserGoals; // Already sorted desc, so [0] is closest
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the start of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const getLogForDate = (day: number) => {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );
    const dateStr = getLocalDateString(date);
    return dailyLogs.find((log) => log.date === dateStr);
  };

  // Helper to check if a day is a rest day, using goal history for past dates.
  //
  // Logic:
  // - Today / future: always use current goals.
  // - Past date with a goal_history snapshot: use that snapshot (accurate).
  // - Past date with NO snapshot (user predates history feature, or date is before
  //   first save): only apply current goals if within the current week — older weeks
  //   must not be retroactively marked as rest days, preserving existing behaviour.
  const isRestDayForDate = (date: Date): boolean => {
    const dateStr = getLocalDateString(date);
    const effectiveSelectedChild = selectedChildId ?? localSelectedChildId;
    const todayStr = getLocalDateString(new Date());
    const isPast = dateStr < todayStr;

    const resolveGoals = (childId: string | null, currentGoals: object) => {
      if (!isPast) return currentGoals; // today/future always use current
      const historical = getGoalsForPastDate(dateStr, childId);
      if (historical) return historical; // accurate snapshot found
      // No history at all for this user yet — fall back to current goals for all
      // past dates so existing users see the same behaviour as before. Once they
      // save goals, history builds and future changes won't retroactively apply.
      return currentGoals;
    };

    if (effectiveSelectedChild) {
      const goals = resolveGoals(effectiveSelectedChild, childGoals[effectiveSelectedChild] ?? {});
      if (!goals) return false;
      return getGoalForDate(goals, dateStr) === null;
    } else if (
      treatmentType === "child" &&
      children.length > 0 &&
      Object.keys(childGoals).length > 0
    ) {
      return children.every((child) => {
        const goals = resolveGoals(child.id, childGoals[child.id] ?? {});
        if (!goals) return false;
        return getGoalForDate(goals, dateStr) === null;
      });
    } else {
      const goals = resolveGoals(null, userGoals);
      if (!goals) return false;
      return getGoalForDate(goals, dateStr) === null;
    }
  };

  const getDayColor = (log: DailyLog | undefined, day: number) => {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isFuture = date > today;
    const isToday = getLocalDateString(date) === getLocalDateString(new Date());

    // If no log exists, determine if it should be red or gray
    if (!log) {
      // isRestDayForDate uses goal_history for past dates and current goals for
      // today/future, so this is always accurate.
      const isRestDay = isRestDayForDate(date);
      if (isRestDay) {
        return "bg-green-700 text-white"; // Rest day = green
      }

      // If it's a future date (non-rest day), show gray
      if (isFuture) {
        return darkMode
          ? "bg-gray-700 text-gray-300"
          : "bg-gray-100 text-gray-600";
      }

      // If we have a join date and this date is after join date, show red (missing data)
      if (userJoinDate) {
        const joinDate = new Date(userJoinDate);
        if (date >= joinDate) {
          return "bg-red-400 text-white"; // Missing data during treatment period
        }
      }

      // Before join date, show gray
      return darkMode
        ? "bg-gray-700 text-gray-300"
        : "bg-gray-100 text-gray-600";
    }

    // If log exists, compute percentage
    let percentage = 0;
    const effectiveSelectedChild = selectedChildId ?? localSelectedChildId;
    const dateStr = getLocalDateString(date);

    // For today, always use current goal (user may have changed it after logging).
    // For past days, prefer historical goal from the log for accuracy.
    let historicalGoal = log.daily_goal_at_time;

    // Specific child selected: compute based on that child's goal
    if (effectiveSelectedChild) {
      const currentGoal = getGoalForDate(
        childGoals[effectiveSelectedChild] || {},
        dateStr,
      );
      const goal =
        isToday && currentGoal !== undefined
          ? currentGoal
          : historicalGoal !== undefined
            ? historicalGoal
            : currentGoal;
      if (goal === null) {
        return "bg-green-700 text-white"; // Rest day = green
      }
      percentage = goal != null && goal > 0 ? (log.total_minutes / goal) * 100 : 0;
    } else if (
      treatmentType === "child" &&
      children.length > 0 &&
      Object.keys(childGoals).length > 0
    ) {
      // All children view: average of each child's percentage
      const childCount = children.length;
      if (childCount > 0) {
        const sumPercentages = children.reduce((sum, child) => {
          const historicalChildGoal =
            log.children_data?.[child.id]?.dailyGoalAtTime;
          const currentChildGoal = getGoalForDate(
            childGoals[child.id] || {},
            dateStr,
          );
          const goal =
            isToday && currentChildGoal !== undefined
              ? currentChildGoal
              : historicalChildGoal !== undefined
                ? historicalChildGoal
                : currentChildGoal;
          if (goal === null) {
            return sum + 100; // Rest day counts as 100%
          }
          const minutes = log.children_data?.[child.id]?.minutes || 0;
          const pct = goal != null && goal > 0 ? (minutes / goal) * 100 : 0;
          return sum + pct;
        }, 0);
        percentage = sumPercentages / childCount;
      } else {
        percentage = 0;
      }
    } else {
      // Legacy/self view
      const currentDayGoal = getGoalForDate(userGoals, dateStr);
      const effectiveGoal =
        isToday && currentDayGoal !== undefined
          ? currentDayGoal
          : historicalGoal !== undefined
            ? historicalGoal
            : currentDayGoal;
      if (effectiveGoal === null) {
        return "bg-green-700 text-white"; // Rest day = green
      }
      const goal = effectiveGoal ?? 0;
      percentage = goal > 0 ? (log.total_minutes / goal) * 100 : 0;
    }

    if (percentage >= 100) return "bg-green-700 text-white";
    if (percentage >= 75) return "bg-green-400 text-white";
    if (percentage >= 50) return "bg-yellow-400 text-gray-900";
    if (percentage >= 25) return "bg-orange-400 text-white";
    return "bg-red-400 text-white";
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round((minutes % 60) * 10) / 10; // Round to 1 decimal
    return `${hours}h ${mins}m`;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const year = prev.getFullYear();
      const month = prev.getMonth();

      if (direction === "prev") {
        // Go to previous month
        if (month === 0) {
          return new Date(year - 1, 11, 1); // December of previous year
        } else {
          return new Date(year, month - 1, 1);
        }
      } else {
        // Go to next month
        if (month === 11) {
          return new Date(year + 1, 0, 1); // January of next year
        } else {
          return new Date(year, month + 1, 1);
        }
      }
    });
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const days = getDaysInMonth();

  // Calculate monthly stats
  const monthlyTotal = dailyLogs.reduce(
    (sum, log) => sum + log.total_minutes,
    0,
  );
  const monthlyAverage =
    dailyLogs.length > 0 ? monthlyTotal / dailyLogs.length : 0;
  const daysWithData = dailyLogs.filter((log) => log.total_minutes > 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative w-full">
        {/* Centered heading */}
        <div className="flex items-center justify-center w-full">
          <h2
            className={`text-2xl font-bold ${
              darkMode ? "text-white" : "text-brand-dark-blue"
            }`}
          >
            Activity Calendar
          </h2>
        </div>
        {treatmentType === "child" &&
          children.length > 0 &&
          !hideChildSelector && (
            <div className="flex justify-center mt-2">
              <div className="flex items-center space-x-2">
                <span
                  className={`text-sm font-medium ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Child:
                </span>
                <select
                  value={(selectedChildId ?? localSelectedChildId) || "all"}
                  onChange={(e) => {
                    const childId =
                      e.target.value === "all" ? undefined : e.target.value;
                    // If parent doesn't control selection, store locally for normal users
                    if (selectedChildId === undefined) {
                      setLocalSelectedChildId(childId);
                    } else {
                      // Persist selection in URL for doctor-controlled views
                      const url = new URL(window.location.href);
                      if (childId) url.searchParams.set("childId", childId);
                      else url.searchParams.delete("childId");
                      window.history.replaceState({}, "", url.toString());
                    }
                    // Trigger reload
                    setCurrentDate((d) => new Date(d));
                  }}
                  className={`px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-brand-cyan focus:border-transparent ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="all">All Children</option>
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

        {/* Month navigation - responsive layout */}
        {/* Large screens: positioned on the right */}
        <div className="hidden lg:block absolute top-0 right-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigateMonth("prev")}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              ←
            </button>
            <h3
              className={`text-xl font-semibold min-w-[200px] text-center ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button
              onClick={() => navigateMonth("next")}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              →
            </button>
          </div>
        </div>

        {/* Small/Medium screens: centered under the heading */}
        <div className="lg:hidden mt-4 flex items-center justify-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigateMonth("prev")}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              ←
            </button>
            <h3
              className={`text-lg sm:text-xl font-semibold min-w-[180px] sm:min-w-[200px] text-center ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button
              onClick={() => navigateMonth("next")}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              →
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div
        className={`rounded-lg p-4 ${
          darkMode ? "bg-gray-800" : "bg-white"
        } shadow-lg`}
      >
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {dayNames.map((day) => (
            <div
              key={day}
              className={`text-center font-semibold py-2 ${
                darkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="h-20"></div>;
            }

            const log = getLogForDate(day);
            const isToday =
              getLocalDateString() ===
              getLocalDateString(
                new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth(),
                  day,
                ),
              );

            const dateForDay = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              day,
            );
            // isRestDayForDate uses goal_history for past dates — always accurate
            const isRestDay = isRestDayForDate(dateForDay);

            return (
              <div
                key={`${currentDate.getFullYear()}-${currentDate.getMonth()}-${day}`}
                onClick={() => setSelectedDay({ day, log })}
                className={`h-20 p-2 rounded-lg border-2 transition-all cursor-pointer hover:scale-105 ${
                  isToday ? "border-brand-cyan" : "border-transparent"
                } ${getDayColor(log, day)} relative overflow-hidden`}
              >
                <div className="font-semibold text-sm flex items-center justify-between">
                  <span>{day}</span>
                  {isRestDay && (
                    <span
                      className="text-xs font-bold bg-white/20 px-1.5 py-0.5 rounded"
                      title="Rest Day"
                    >
                      🛌
                    </span>
                  )}
                </div>
                {log && (
                  <div className="text-xs mt-1">
                    {formatTime(log.total_minutes)}
                    {log.entries.length > 1 && (
                      <div className="text-xs text-gray-400">
                        {log.entries.length} entries
                      </div>
                    )}
                  </div>
                )}
                {log?.hasNotes && (
                  <div className="absolute bottom-1 right-1">
                    <div className="w-2 h-2 bg-blue-300 rounded-full"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly Summary */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-4`}>
        <div
          className={`p-4 rounded-lg ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-lg`}
        >
          <h4
            className={`font-semibold mb-2 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Monthly Total
          </h4>
          <p
            className={`text-2xl font-bold ${
              darkMode ? "text-green-400" : "text-green-600"
            }`}
          >
            {formatTime(monthlyTotal)}
          </p>
        </div>

        <div
          className={`p-4 rounded-lg ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-lg`}
        >
          <h4
            className={`font-semibold mb-2 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Daily Average
          </h4>
          <p
            className={`text-2xl font-bold ${
              darkMode ? "text-blue-400" : "text-blue-600"
            }`}
          >
            {formatTime(Math.round(monthlyAverage))}
          </p>
        </div>

        <div
          className={`p-4 rounded-lg ${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-lg`}
        >
          <h4
            className={`font-semibold mb-2 ${
              darkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Active Days
          </h4>
          <p
            className={`text-2xl font-bold ${
              darkMode ? "text-purple-400" : "text-purple-600"
            }`}
          >
            {daysWithData}
          </p>
        </div>
      </div>

      {/* Legend */}
      <div
        className={`p-4 rounded-lg ${
          darkMode ? "bg-gray-800" : "bg-white"
        } shadow-lg`}
      >
        <h4
          className={`font-semibold mb-3 ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Goal Achievement Legend
        </h4>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-700 rounded"></div>
            <span className={darkMode ? "text-gray-300" : "text-gray-700"}>
              100%+ Goal
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-700 rounded flex items-center justify-center">
              <span className="text-xs">🛌</span>
            </div>
            <span className={darkMode ? "text-gray-300" : "text-gray-700"}>
              Rest Day
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-400 rounded"></div>
            <span className={darkMode ? "text-gray-300" : "text-gray-700"}>
              75-99%
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-400 rounded"></div>
            <span className={darkMode ? "text-gray-300" : "text-gray-700"}>
              50-74%
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-orange-400 rounded"></div>
            <span className={darkMode ? "text-gray-300" : "text-gray-700"}>
              25-49%
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-400 rounded"></div>
            <span className={darkMode ? "text-gray-300" : "text-gray-700"}>
              0-24%
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`w-4 h-4 rounded ${
                darkMode ? "bg-gray-700" : "bg-gray-100"
              }`}
            ></div>
            <span className={darkMode ? "text-gray-300" : "text-gray-700"}>
              No Data
            </span>
          </div>
        </div>
      </div>

      {/* Day Detail Modal */}
      {selectedDay && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedDay(null)}
        >
          <div
            className={`p-6 rounded-lg max-w-md w-full mx-4 ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className={`text-xl font-semibold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {monthNames[currentDate.getMonth()]} {selectedDay.day},{" "}
                {currentDate.getFullYear()}
              </h3>
              <button
                onClick={() => setSelectedDay(null)}
                className={`text-2xl ${
                  darkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                ×
              </button>
            </div>

            {selectedDay.log ? (
              <div className="space-y-4">
                <div>
                  <h4
                    className={`font-semibold mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Total Time Spent
                  </h4>
                  <p
                    className={`text-lg ${
                      darkMode ? "text-green-400" : "text-green-600"
                    }`}
                  >
                    {formatTime(selectedDay.log.total_minutes)}
                  </p>
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {(() => {
                      const dateObj = new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        selectedDay.day,
                      );
                      const dateStr = getLocalDateString(dateObj);
                      const isToday =
                        dateStr === getLocalDateString(new Date());
                      const currentGoal = getGoalForDate(userGoals, dateStr);
                      // For today, always use current goal
                      const goalToUse =
                        isToday && currentGoal !== undefined
                          ? currentGoal
                          : selectedDay.log.daily_goal_at_time !== undefined
                            ? selectedDay.log.daily_goal_at_time
                            : currentGoal;
                      if (goalToUse === null) {
                        return "Goal: Rest day";
                      }
                      if (!goalToUse) return null;
                      return `Goal: ${formatTime(goalToUse)} (${Math.round(
                        (selectedDay.log.total_minutes / goalToUse) * 100,
                      )}%)`;
                    })()}
                  </div>
                </div>

                {/* Child Information */}
                {treatmentType === "child" &&
                  selectedDay.log.children_data &&
                  Object.keys(selectedDay.log.children_data).length > 0 && (
                    <div>
                      <h4
                        className={`font-semibold mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Children's Progress
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(selectedDay.log.children_data).map(
                          ([childId, childData]) => (
                            <div
                              key={childId}
                              className={`p-3 rounded-lg ${
                                darkMode ? "bg-gray-700" : "bg-gray-100"
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <span
                                    className={`font-medium ${
                                      darkMode
                                        ? "text-blue-400"
                                        : "text-blue-600"
                                    }`}
                                  >
                                    {childData.name}
                                  </span>
                                  <div
                                    className={`text-sm ${
                                      darkMode
                                        ? "text-green-400"
                                        : "text-green-600"
                                    }`}
                                  >
                                    {formatTime(childData.minutes)}
                                  </div>
                                </div>
                                <div
                                  className={`text-xs ${
                                    darkMode ? "text-gray-400" : "text-gray-500"
                                  }`}
                                >
                                  {(() => {
                                    const historicalGoal =
                                      childData.dailyGoalAtTime;
                                    const dateObj = new Date(
                                      currentDate.getFullYear(),
                                      currentDate.getMonth(),
                                      selectedDay.day,
                                    );
                                    const dateStr =
                                      getLocalDateString(dateObj);
                                    const isToday =
                                      dateStr ===
                                      getLocalDateString(new Date());
                                    const currentGoal = getGoalForDate(
                                      childGoals[childId] || {},
                                      dateStr,
                                    );
                                    const goalToUse =
                                      isToday && currentGoal !== undefined
                                        ? currentGoal
                                        : historicalGoal !== undefined
                                          ? historicalGoal
                                          : currentGoal;
                                    if (goalToUse === null) {
                                      return "Rest day";
                                    }
                                    if (!goalToUse) return null;
                                    return `${Math.round((childData.minutes / goalToUse) * 100)}% of goal`;
                                  })()}
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                <div>
                  <h4
                    className={`font-semibold mb-2 ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Activity Entries ({selectedDay.log.entries.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedDay.log.entries.map((entry, index) => (
                      <div
                        key={entry.id}
                        className={`p-3 rounded-lg ${
                          darkMode ? "bg-gray-700" : "bg-gray-100"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <span
                              className={`font-medium ${
                                darkMode ? "text-green-400" : "text-green-600"
                              }`}
                            >
                              {formatTime(entry.time_spent_minutes)}
                            </span>
                            <div className="flex items-center space-x-2">
                              <div
                                className={`text-xs ${
                                  darkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                Entry #{index + 1}
                              </div>
                              {entry.child_name && (
                                <div
                                  className={`text-xs px-2 py-1 rounded ${
                                    darkMode
                                      ? "bg-blue-600 text-white"
                                      : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {entry.child_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {entry.notes && (
                          <div
                            className={`mt-2 text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            {entry.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`text-center py-8 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <p>No activity logged for this day.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
